import React, { Component, ComponentType } from 'react'
import { Dayjs } from 'dayjs'
import { styled } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker as MUIDatePicker, LocalizationProvider, DatePickerProps } from '@mui/x-date-pickers'
import { IFormInputBase } from './types'
import { getErrorMessage } from './helpers'
import { dayjsCustom, mergeObjects } from '../utils'

const formatDefault = 'MM/DD/YYYY'

export interface IDatePickerSlots {
  defaultValue?: string
  minDate?: Dayjs
  minDateOffset?: number
  datePickerProps?: DatePickerProps<Dayjs>
}

export interface IDatePickerProps<T> extends IFormInputBase<T> {
  format?: string
  slots?: Omit<IDatePickerSlots, 'defaultValue'>
}

export interface IDatePickerState {
  value: Dayjs | null
}

const CreateDatePicker = function <T extends Object>(params?: IDatePickerSlots): ComponentType<IDatePickerProps<T>> {
  class DatePicker extends Component<IDatePickerProps<T>, IDatePickerState> {
    private _cachedSlots: IDatePickerSlots = {}
    constructor(props: IDatePickerProps<T>) {
      super(props)
      this._cachedSlots = this.mergeSlots(props.slots) ?? {}
      this.state = { value: this.getDefaultValue() }
    }

    get slots() {
      return this._cachedSlots
    }

    componentDidUpdate(prevProps: IDatePickerProps<T>) {
      if (prevProps.slots !== this.props.slots) {
        this._cachedSlots = this.mergeSlots(this.props.slots)
      }
    }

    getDatePickerProps = (): DatePickerProps<Dayjs> => {
      const delayInDays = params?.minDateOffset ?? 0
      const minDate = delayInDays > 0 ? dayjsCustom().add(delayInDays, 'day').startOf('day') : params?.minDate
      const label = this.props.label ?? this.props.name?.toString()
      const format = this.props.format ?? formatDefault
      const eMessage = getErrorMessage<T>(this.props.messageErrors, this.props.name)
      const obj: DatePickerProps<Dayjs> = {
        label,
        format,
        views: ['day', 'month', 'year'],
        value: this.state.value,
        onChange: this.handleChange,
        disabled: this.props.disabled,
        minDate,
        slotProps: {
          textField: { onBlur: this.handleBlur, fullWidth: true, error: eMessage.error, helperText: eMessage.message, variant: 'outlined' }
        }
      }
      return mergeObjects(obj, params?.datePickerProps, this.slots?.datePickerProps)
    }

    refInput: HTMLInputElement | null = null
    render() {
      return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <CustomDatePicker {...this.getDatePickerProps()} />
          <input
            hidden
            name={this.props.name?.toString()}
            defaultValue={this.getDefaultValue()?.toDate().toISOString()}
            ref={(ref) => (this.refInput = ref)}
          />
        </LocalizationProvider>
      )
    }

    private mergeSlots = (currentSlots?: IDatePickerSlots): IDatePickerSlots => {
      return mergeObjects<IDatePickerSlots>({}, params, currentSlots)
    }

    handleBlur = () => {
      if (!this.props.name) return
      this.props.onBlur && this.props.onBlur(this.props.name)
    }

    handleChange = (newValue: Dayjs | null) => {
      this.setState({ value: newValue })
      if (this.refInput) {
        this.refInput.value = newValue && !isNaN(newValue.toDate().getTime()) ? newValue.toDate().toISOString() : ''
      }
      setTimeout(this.handleBlur, 50)
    }

    getDefaultValue = (): Dayjs => {
      try {
        if (!this.props.defaultValue && !this.slots.defaultValue && this.slots.minDateOffset) return dayjsCustom().add(1, 'day').startOf('day')
        if (!this.props.defaultValue && this.slots.minDate) return this.slots.minDate

        const { data, name } = this.props
        const dValue = this.props.defaultValue ?? this.slots.defaultValue ?? (name ? data?.[name] : '')
        return dValue ? dayjsCustom(dValue.toString()) : dayjsCustom()
      } catch {
        return dayjsCustom()
      }
    }
  }
  return DatePicker
}

export default CreateDatePicker

const CustomDatePicker = styled(MUIDatePicker<Dayjs>)({
  '& .MuiInputBase-root::before, & .MuiInputBase-root::after': {
    borderBottom: 'none !important'
  }
})
